import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { HeadContentData } from './types';

interface AdvancedTabProps {
  formData: HeadContentData;
  onAddCustomMeta: () => void;
  onCustomMetaChange: (
    index: number,
    field: 'name' | 'property' | 'content',
    value: string
  ) => void;
  onRemoveCustomMeta: (index: number) => void;
  onAddStructuredData: () => void;
  onStructuredDataChange: (index: number, value: string) => void;
  onRemoveStructuredData: (index: number) => void;
}

export const AdvancedTab: React.FC<AdvancedTabProps> = ({
  formData,
  onAddCustomMeta,
  onCustomMetaChange,
  onRemoveCustomMeta,
  onAddStructuredData,
  onStructuredDataChange,
  onRemoveStructuredData,
}) => (
  <div className="space-y-6">
    {/* Custom Meta Tags */}
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Custom Meta Tags</h3>
        <button
          type="button"
          onClick={onAddCustomMeta}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Meta Tag
        </button>
      </div>

      {formData.customMeta.length === 0 ? (
        <p className="text-sm text-gray-500">No custom meta tags added yet.</p>
      ) : (
        <div className="space-y-3">
          {formData.customMeta.map((meta: any, index: number) => (
            <div key={index} className="flex gap-2 items-start">
              <select
                value={meta.property ? 'property' : 'name'}
                onChange={e => {
                  const type = e.target.value;
                  if (type === 'property') {
                    onCustomMetaChange(index, 'property', meta.name || '');
                    onCustomMetaChange(index, 'name', '');
                  } else {
                    onCustomMetaChange(index, 'name', meta.property || '');
                    onCustomMetaChange(index, 'property', '');
                  }
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="name">name</option>
                <option value="property">property</option>
              </select>
              <input
                type="text"
                value={meta.name || meta.property || ''}
                onChange={e =>
                  onCustomMetaChange(index, meta.property ? 'property' : 'name', e.target.value)
                }
                placeholder="Meta name/property"
                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <input
                type="text"
                value={meta.content || ''}
                onChange={e => onCustomMetaChange(index, 'content', e.target.value)}
                placeholder="Content"
                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
              />
              <button
                type="button"
                onClick={() => onRemoveCustomMeta(index)}
                className="p-1 text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Structured Data */}
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">Structured Data (JSON-LD)</h3>
        <button
          type="button"
          onClick={onAddStructuredData}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Schema
        </button>
      </div>

      {formData.structuredData.length === 0 ? (
        <p className="text-sm text-gray-500">No structured data added yet.</p>
      ) : (
        <div className="space-y-3">
          {formData.structuredData.map((data: any, index: number) => (
            <div key={index} className="relative">
              <textarea
                value={JSON.stringify(data, null, 2)}
                onChange={e => onStructuredDataChange(index, e.target.value)}
                placeholder='{"@context": "https://schema.org", "@type": "WebPage"}'
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => onRemoveStructuredData(index)}
                className="absolute top-2 right-2 p-1 text-red-500 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);
